import { AiLabsSection } from '@educator/components/ProgramSections/AiLabsSection'
import { CurriculumSection } from '@educator/components/ProgramSections/CurriculumSection'
import { InstructorsSection } from '@educator/components/ProgramSections/InstructorsSection'
import { TestimonialsSection } from '@educator/components/ProgramSections/TestimonialsSection'
import type {
  ProgramSection,
  ProgramSectionContext,
} from '@educator/components/ProgramSections/types/sections'
import {
  TrackVisible,
  TrackingAttributes,
} from '@educator/tracking/educatorTracking'
import { AdmissionsSection } from './AdmissionsSection'
import { CtaSection } from './CtaSection'
import { HeroSection } from './HeroSection'
import { PreviousCohortSection } from './PreviousCohortSection'
import { ProgramDetailsFaqSection } from './ProgramDetailsFaqSection'
import { ValuePromiseSection } from './ValuePromiseSection'

interface ProgramSectionsProps extends ProgramSectionContext {
  sections: ProgramSection[]
}

/** Rows are taller than the viewport, so the 0.7 default would never report. */
const ROW_VISIBILITY_CONFIG = { threshold: 0 }

const renderSection = (
  section: ProgramSection,
  context: ProgramSectionContext,
) => {
  switch (section.type) {
    case 'hero':
      return <HeroSection data={section.data} {...context} />
    case 'valuePromise':
      return <ValuePromiseSection />
    case 'testimonials':
      return <TestimonialsSection data={section.data} />
    case 'aiLabs':
      return <AiLabsSection data={section.data} {...context} />
    case 'instructors':
      return <InstructorsSection data={section.data} />
    case 'curriculum':
      return <CurriculumSection data={section.data} />
    case 'previousCohort':
      return <PreviousCohortSection />
    case 'cta':
      return <CtaSection data={section.data} {...context} />
    case 'admissions':
      return <AdmissionsSection />
    case 'faq':
      return <ProgramDetailsFaqSection />
    default:
      return null
  }
}

/**
 * Renders whatever the payload lists, in the order it lists it. Section types it
 * does not recognise are skipped, so a backend shipping a new section ahead of
 * the client degrades to a missing block rather than a crash.
 *
 * Row tracking lives here rather than in the page so `row_position_index`
 * follows the payload order instead of being hardcoded by the caller.
 */
export const ProgramSections = ({
  sections,
  ...context
}: ProgramSectionsProps) => (
  <>
    {sections.map((section, index) => {
      const rendered = renderSection(section, context)
      if (!rendered) return null

      // Row identity via context so scroll/view-more events join Row Viewed
      // without prop-drilling.
      return (
        <TrackingAttributes
          key={section.id}
          row_name={section.id}
          row_position_index={index}
        >
          <TrackVisible
            event='educatorRowViewed'
            visibilityConfig={ROW_VISIBILITY_CONFIG}
          >
            {rendered}
          </TrackVisible>
        </TrackingAttributes>
      )
    })}
  </>
)
